import React, { useMemo, useState, useRef, useCallback } from 'react';
import './ChatMarkdown.css';

// ── Inline renderer ──────────────────────────────────────────────────────────
// Order matters: escape HTML first, then restore safe tags, then apply markdown.
function inlineHTML(raw) {
  return raw
    // 1. Escape HTML
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

    // 2. Restore <br> tags (safe, explicit allow-list)
    .replace(/&lt;br\s*\/?&gt;/gi, '<br>')

    // 3. Angle-bracket URLs  <https://...>
    .replace(/&lt;(https?:\/\/[^&\s"]+)&gt;/g, (_, url) =>
      url.includes('calendly.com')
        ? `<a href="${url}" target="_blank" rel="noreferrer" class="md-cta">📅 Book Free Consultation →</a>`
        : `<a href="${url}" target="_blank" rel="noreferrer" class="md-link">${url}</a>`
    )

    // 4. Markdown inline styles
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code class="md-ic">$1</code>')

    // 5. Markdown links [text](url)
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (_, t, url) =>
      url.includes('calendly.com')
        ? `<a href="${url}" target="_blank" rel="noreferrer" class="md-cta">📅 ${t}</a>`
        : `<a href="${url}" target="_blank" rel="noreferrer" class="md-link">${t}</a>`
    )

    // 6. Bare URLs (not already wrapped in an anchor)
    .replace(/(^|[\s—–:])((https?:\/\/)[^\s<"&]+)/g, (_, pre, url) =>
      url.includes('calendly.com')
        ? `${pre}<a href="${url}" target="_blank" rel="noreferrer" class="md-cta">📅 Book Free Consultation →</a>`
        : `${pre}<a href="${url}" target="_blank" rel="noreferrer" class="md-link">${url}</a>`
    );
}

function Inline({ text }) {
  return <span dangerouslySetInnerHTML={{ __html: inlineHTML(text) }} />;
}

// ── Helpers ──────────────────────────────────────────────────────────────────
// Split a table row — handles both pipe-delimited and tab-delimited
const splitRow = (line, sep) =>
  sep === '\t'
    ? line.split('\t').map(c => c.trim())
    : line.split('|').map(c => c.trim()).filter(Boolean);

// Returns true if `line` looks like a pipe/tab table separator (---|---|  or ---\t---)
const isSepRow = (line) =>
  /^[\|\-:\s\t]+$/.test(line) && /[-]{2,}/.test(line);

// Detect how many tab-separated columns a line has (≥2 = candidate table row)
const tabCols = (line) => {
  if (!line.includes('\t')) return 0;
  return line.split('\t').filter(c => c.trim()).length;
};

// ── Block parser ──────────────────────────────────────────────────────────────
function parseBlocks(raw) {
  const lines = raw.split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line  = lines[i];
    const trimmed = line.trim();

    // ── Fenced code block
    if (trimmed.startsWith('```')) {
      const lang = trimmed.slice(3).trim();
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      blocks.push({ type: 'code', lang, content: codeLines.join('\n') });
      i++;
      continue;
    }

    // ── Heading
    const hm = trimmed.match(/^(#{1,6})\s+(.+)/);
    if (hm) {
      blocks.push({ type: 'heading', level: hm[1].length, text: hm[2] });
      i++;
      continue;
    }

    // ── Pipe table  (row | sep | rows…  OR  row | rows… with no sep)
    if (trimmed.includes('|') && !trimmed.startsWith('#')) {
      const hasSep =
        i + 1 < lines.length && isSepRow(lines[i + 1]);

      // Confirm it actually looks like a table: at least 2 pipe-separated cells
      const potentialHeaders = trimmed.split('|').map(c => c.trim()).filter(Boolean);
      if (potentialHeaders.length >= 2) {
        const headers = potentialHeaders;
        if (hasSep) i += 2; else i += 1; // skip header (+ sep if present)

        const rows = [];
        while (i < lines.length) {
          const rowTrim = lines[i].trim();
          if (!rowTrim || !rowTrim.includes('|')) break;
          if (isSepRow(rowTrim)) { i++; continue; } // skip extra sep rows
          rows.push(splitRow(rowTrim, '|'));
          i++;
        }
        blocks.push({ type: 'table', headers, rows });
        continue;
      }
    }

    // ── Tab-separated table  (no pipes; AI sometimes generates these)
    if (tabCols(line) >= 2) {
      // Collect all consecutive tab-row lines
      const allRows = [];
      while (i < lines.length && tabCols(lines[i]) >= 2) {
        if (!isSepRow(lines[i])) {           // skip separator rows like ---\t---
          allRows.push(lines[i].split('\t').map(c => c.trim()));
        }
        i++;
      }
      if (allRows.length >= 2) {
        const headers = allRows[0];
        const rows    = allRows.slice(1);
        blocks.push({ type: 'table', headers, rows });
      } else if (allRows.length === 1) {
        // single tab-row → treat as paragraph
        blocks.push({ type: 'paragraph', text: allRows[0].join('  ') });
      }
      continue;
    }

    // ── Horizontal rule
    if (/^[-*_]{3,}$/.test(trimmed)) {
      blocks.push({ type: 'hr' });
      i++;
      continue;
    }

    // ── Blockquote
    if (trimmed.startsWith('>')) {
      const qlines = [];
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        qlines.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      blocks.push({ type: 'blockquote', text: qlines.join(' ') });
      continue;
    }

    // ── Unordered list
    if (/^[-*•]\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^[-*•]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*•]\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    // ── Ordered list
    if (/^\d+[.)]\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\d+[.)]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+[.)]\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    // ── Empty line
    if (!trimmed) { i++; continue; }

    // ── Paragraph
    const paraLines = [];
    while (i < lines.length) {
      const l = lines[i];
      const lt = l.trim();
      if (
        !lt ||
        lt.startsWith('#') ||
        lt.startsWith('```') ||
        lt.startsWith('>') ||
        /^[-*•]\s+/.test(lt) ||
        /^\d+[.)]\s+/.test(lt) ||
        /^[-*_]{3,}$/.test(lt) ||
        tabCols(l) >= 2 ||
        (lt.includes('|') && lt.split('|').filter(Boolean).length >= 2)
      ) break;
      paraLines.push(lt);
      i++;
    }
    if (paraLines.length) blocks.push({ type: 'paragraph', text: paraLines.join(' ') });
  }

  return blocks;
}

// ── HTML detection ────────────────────────────────────────────────────────────
function isHTMLBlock(lang, content) {
  if (['html', 'htm'].includes((lang || '').toLowerCase())) return true;
  const t = content.trim().toLowerCase();
  return t.startsWith('<!doctype html') || t.startsWith('<html');
}

// ── HTML preview + code toggle ────────────────────────────────────────────────
function HTMLCodeBlock({ lang, content }) {
  const [tab, setTab] = useState('preview');
  const iframeRef = useRef(null);

  const autoResize = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const h = iframe.contentDocument?.documentElement?.scrollHeight;
      if (h) iframe.style.height = `${Math.min(Math.max(h + 8, 80), 600)}px`;
    } catch (_) {}
  }, []);

  return (
    <div className="md-code-wrap md-html-wrap">
      <div className="md-code-header">
        <span className="md-code-lang">{lang || 'html'}</span>
        <div className="md-tab-group">
          <button
            className={`md-tab ${tab === 'preview' ? 'active' : ''}`}
            onClick={() => setTab('preview')}
          >
            ▶ Preview
          </button>
          <button
            className={`md-tab ${tab === 'code' ? 'active' : ''}`}
            onClick={() => setTab('code')}
          >
            {'<>'} Code
          </button>
        </div>
      </div>

      {tab === 'preview' ? (
        <iframe
          ref={iframeRef}
          srcDoc={content}
          sandbox="allow-scripts allow-same-origin allow-forms"
          className="md-html-preview"
          title="HTML Preview"
          onLoad={autoResize}
        />
      ) : (
        <pre className="md-code"><code>{content}</code></pre>
      )}
    </div>
  );
}

// ── Block renderers ───────────────────────────────────────────────────────────
function Block({ block, index }) {
  const style = { animationDelay: `${Math.min(index * 55, 700)}ms` };

  switch (block.type) {
    case 'heading': {
      const lvl = Math.min(block.level + 1, 6);
      const Tag = `h${lvl}`;
      return (
        <div className="md-block" style={style}>
          <Tag className={`md-h md-h${block.level}`}><Inline text={block.text} /></Tag>
        </div>
      );
    }

    case 'paragraph':
      return (
        <div className="md-block" style={style}>
          <p className="md-p"><Inline text={block.text} /></p>
        </div>
      );

    case 'ul':
      return (
        <div className="md-block" style={style}>
          <ul className="md-ul">
            {block.items.map((item, j) => <li key={j}><Inline text={item} /></li>)}
          </ul>
        </div>
      );

    case 'ol':
      return (
        <div className="md-block" style={style}>
          <ol className="md-ol">
            {block.items.map((item, j) => <li key={j}><Inline text={item} /></li>)}
          </ol>
        </div>
      );

    case 'code':
      return (
        <div className="md-block" style={style}>
          {isHTMLBlock(block.lang, block.content)
            ? <HTMLCodeBlock lang={block.lang} content={block.content} />
            : (
              <div className="md-code-wrap">
                <div className="md-code-header">
                  {block.lang && <span className="md-code-lang">{block.lang}</span>}
                </div>
                <pre className="md-code"><code>{block.content}</code></pre>
              </div>
            )
          }
        </div>
      );

    case 'table':
      return (
        <div className="md-block md-table-outer" style={style}>
          <div className="md-table-scroll">
            <table className="md-table">
              <thead>
                <tr>
                  {block.headers.map((h, j) => (
                    <th key={j}><Inline text={h} /></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, ri) => (
                  <tr key={ri}>
                    {block.headers.map((_, ci) => (
                      <td key={ci}><Inline text={row[ci] ?? ''} /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );

    case 'blockquote':
      return (
        <div className="md-block" style={style}>
          <blockquote className="md-blockquote"><Inline text={block.text} /></blockquote>
        </div>
      );

    case 'hr':
      return <hr className="md-hr" style={style} />;

    default:
      return null;
  }
}

// ── Public component ──────────────────────────────────────────────────────────
export default function ChatMarkdown({ content }) {
  const blocks = useMemo(() => parseBlocks(content || ''), [content]);
  return (
    <div className="md-root">
      {blocks.map((block, j) => <Block key={j} block={block} index={j} />)}
    </div>
  );
}
